import {AbstractControl, ValidationErrors, ValidatorFn} from "@angular/forms";

export function matchValidator(
  matchTo: string,
  reverse?: boolean
): ValidatorFn {
  return (control: AbstractControl):
    ValidationErrors | null => {
    if (control.parent && reverse) {
      const c = (control.parent?.controls as any)[matchTo] as AbstractControl;
      if (c) {
        c.updateValueAndValidity();
      }
      return null;
    }
    return !!control.parent &&
    !!control.parent.value &&
    control.value ===
    (control.parent?.controls as any)[matchTo].value
      ? null
      : { matching: true };
  };
}

/**
 * Validador para cédula dominicana
 * Valida el formato y el dígito verificador de la cédula dominicana
 */
export function dominianIdValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) {
      return null; // Si no hay valor, no validar (usar required por separado)
    }

    const cedula = control.value.toString();
    
    if (!isValidDominianId(cedula)) {
      return { invalidDominianId: true };
    }

    return null;
  };
}

/**
 * Validador condicional para identificación
 * Valida cédula dominicana cuando identificationType = 1
 */
export function conditionalIdValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.parent) {
      return null;
    }

    const identificationType = control.parent.get('identificationType')?.value;
    const identification = control.value;

    // Si no hay valor de identificación, no validar aquí (usar required por separado)
    if (!identification) {
      return null;
    }

    // Solo validar cédula dominicana cuando identificationType = 1
    if (identificationType === 1) {
      if (!isValidDominianId(identification)) {
        return { invalidDominianId: true };
      }
    }

    return null;
  };
}

/**
 * Función auxiliar para validar cédula dominicana
 * @param cedula - Cédula a validar (puede contener guiones)
 * @returns true si es válida, false si no
 */
function isValidDominianId(cedula: string): boolean {
  if (!cedula) return false;

  // Eliminar guiones y espacios
  const c = cedula.replace(/[-\s]/g, '');
  
  // Verificar que tenga exactamente 11 dígitos
  if (c.length !== 11 || !/^\d+$/.test(c)) {
    return false;
  }

  // Obtener los primeros 10 dígitos y el dígito verificador
  const cedulaSinDigito = c.slice(0, -1);
  const digitoVerificador = parseInt(c.slice(-1), 10);
  let suma = 0;

  // Aplicar algoritmo de validación
  for (let i = 0; i < cedulaSinDigito.length; i++) {
    const mod = (i % 2 === 0) ? 1 : 2;
    let res = parseInt(cedulaSinDigito[i], 10) * mod;
    
    // Si el resultado es mayor a 9, sumar los dígitos
    if (res > 9) {
      res = Math.floor(res / 10) + (res % 10);
    }
    
    suma += res;
  }

  // Calcular el dígito verificador esperado
  const digitoCalculado = (10 - (suma % 10)) % 10;
  
  return digitoCalculado === digitoVerificador;
}